<template>
    <div class="body bank-app">
        <Navbar :show-back-button="true" @back="navigateToDashboard"/>
        
        <div class="section-header" style="direction: rtl;">
            <!-- <button class="back-arrow" @click="navigateToDashboard">
                →
            </button> -->
            <h3 class="section-title mt-3" style="margin-right: 10px;">{{ $t('employer.traz.title') }}</h3>
        </div>
        
        <!-- Action Bar -->
        <div class="action-bar">
            <!-- Print Button -->
            <button class="print-btn" @click="printBalanceSheet">
                🖨️ {{ $t('employer.traz.print_balance') }}
            </button>
            
            <!-- Export Button -->
            <button class="export-btn" @click="exportToExcel">
                📥 {{ $t('employer.traz.export_excel') }}
            </button>
            
            <!-- Refresh Button -->
            <button class="refresh-btn" @click="fetchTarazData" :disabled="loading">
                🔄 
                <span v-if="loading">{{ $t('employer.traz.loading') }}</span>
                <span v-else>{{ $t('employer.traz.refresh') }}</span>
            </button>
            
            <!-- Currency Selector -->
            <div class="currency-selector" style="direction: rtl;">
                <label for="currency-select" class="currency-label">{{ $t('employer.traz.currency') }}:</label>
                <select 
                    id="currency-select"
                    v-model="selectedArz" 
                    @change="onArzChange"
                    class="currency-select"
                    :disabled="loadingArz || arzList.length === 0"
                >
                    <option 
                        v-for="arz in arzList" 
                        :key="arz.idd" 
                        :value="arz.idd"
                        :selected="arz.idd === 1"
                    >
                        {{ arz.nam }} ({{ arz.sign }})
                    </option>
                </select>
                <div v-if="loadingArz" class="currency-loading">
                    {{ $t('employer.traz.loading_currencies') }}
                </div>
            </div>

            <!-- Month Selector -->
            <div class="cm-level-selector" style="direction: rtl;">
                <label class="cm-label">📅 {{ $t('employer.traz.month') }}:</label>
                <select 
                    v-model="selectedMonth" 
                    @change="onMonthChange"
                    class="form-select"
                >
                    <option value="">{{ $t('employer.traz.select_month') }}</option>
                    <option v-for="(month, index) in persianMonths" 
                            :key="index" 
                            :value="index + 1">
                        {{ month }}
                    </option>
                </select>
            </div>      

            <!-- CM Level Selector -->
            <div class="cm-level-selector" style="direction: rtl;">
                <label for="cm-select" class="cm-label">{{ $t('employer.traz.display_level') }}:</label>
                <select 
                    id="cm-select"
                    v-model="selectedCmLevel" 
                    @change="onCmLevelChange"
                    class="cm-select"
                    :disabled="loadingArz || arzList.length === 0"
                >
                    <option value="1">{{ $t('employer.traz.level_group') }}</option>
                    <option value="2">{{ $t('employer.traz.level_total') }}</option>
                    <option value="3">{{ $t('employer.traz.level_sub') }}</option>
                    <option value="4">{{ $t('employer.traz.level_detail') }}</option>
                </select>
                <div v-if="loadingArz" class="currency-loading">
                    {{ $t('employer.traz.loading_currencies') }}
                </div>
            </div>
            
            <!-- Search Input -->
            <div class="search-container" style="direction: rtl;">
                <input 
                    type="text" 
                    v-model="searchQuery"
                    :placeholder="$t('employer.traz.search_placeholder')"
                    class="search-input"
                />
                <span class="search-icon">🔍</span>
            </div>
        </div>
        
        <!-- Main Balance Sheet Container -->
        <div class="balance-container">
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <span>{{ $t('employer.traz.loading_data') }}</span>
            </div>
            
            <!-- Error State -->
            <div v-if="error" class="error-state">
                <div class="error-icon">⚠️</div>
                <h4>{{ $t('employer.traz.error_title') }}</h4>
                <p>{{ error }}</p>
                <button @click="fetchTarazData" class="retry-btn">{{ $t('employer.traz.retry') }}</button>
            </div>
            
            <!-- Account Cards -->
            <div v-if="!loading && !error" class="account-cards-container">
                <!-- Info Header -->
                <div class="cards-info-header">
                    <div class="cards-stats">
                        <span class="stat-item">
                            <span class="stat-icon">📅</span>
                            <span>{{ $t('employer.traz.date') }}: {{ currentDate }}</span>
                        </span>
                        <span class="stat-item">
                            <span class="stat-icon">💰</span>
                            <span>{{ $t('employer.traz.currency_label') }}: {{ selectedArzName }}</span>
                        </span>
                        <span class="stat-item">
                            <span class="stat-icon">⏱️</span>
                            <span>{{ $t('employer.traz.period') }}: {{ getDateRangeText() }}</span>
                        </span>
                        <span class="stat-item">
                            <span class="stat-icon">📋</span>
                            <span>{{ $t('employer.traz.total_accounts') }}: {{ filteredAccounts.length }}</span>
                        </span>
                        <span v-if="searchQuery" class="stat-item">
                            <span class="stat-icon">🔍</span>
                            <span>{{ $t('employer.traz.search_results') }}: {{ searchQuery }}</span>
                        </span>
                    </div>
                </div>
                
                <!-- Account Cards Grid -->
                <div class="account-cards-grid">
                    <!-- Account Cards -->
                    <template v-for="(account, index) in paginatedAccounts" :key="index">
                        <!-- Parent Account Card -->
                        <div 
                            :class="[
                                'account-card',
                                `level-${account.Level || 1}`,
                                { 'has-children': account.HasChildren, 'expanded': account.expanded }
                            ]"
                        >
                            <div class="card-header" @click="toggleAccount(account)">
                                <div class="account-code-header">
                                    <span v-if="account.HasChildren" class="expand-icon">
                                        {{ account.expanded ? '▼' : '▶' }}
                                    </span>
                                    <span class="code-badge" :class="`level-${account.Level || 1}-badge`">
                                        {{ account.HandCode }}
                                    </span>
                                </div>
                                <h4 class="account-title">{{ account.HandTitle }}</h4>
                            </div>
                            
                            <div class="card-body">
                                <!-- Opening Balance -->
                                <div class="balance-section">
                                    <h5 class="section-title">
                                        <span class="section-icon">📊</span>
                                        {{ $t('employer.traz.opening_balance') }}
                                    </h5>
                                    <div class="amount-row">
                                        <div class="amount-col">
                                            <span class="amount-label">{{ $t('employer.traz.debit') }}:</span>
                                            <span class="amount-value debit">{{ formatCurrency(account.MandeFirst_Bed) }}</span>
                                        </div>
                                        <div class="amount-col">
                                            <span class="amount-label">{{ $t('employer.traz.credit') }}:</span>
                                            <span class="amount-value credit">{{ formatCurrency(account.MandeFirst_Bes) }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Period Performance -->
                                <div class="balance-section">
                                    <h5 class="section-title">
                                        <span class="section-icon">📈</span>
                                        {{ $t('employer.traz.period_performance') }}
                                    </h5>
                                    <div class="amount-row">
                                        <div class="amount-col">
                                            <span class="amount-label">{{ $t('employer.traz.debit') }}:</span>
                                            <span class="amount-value debit">{{ formatCurrency(account.Sum_Bed) }}</span>
                                        </div>
                                        <div class="amount-col">
                                            <span class="amount-label">{{ $t('employer.traz.credit') }}:</span>
                                            <span class="amount-value credit">{{ formatCurrency(account.Sum_Bes) }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Closing Balance -->
                                <div class="balance-section">
                                    <h5 class="section-title">
                                        <span class="section-icon">💰</span>
                                        {{ $t('employer.traz.closing_balance') }}
                                    </h5>
                                    <div class="amount-row">
                                        <div class="amount-col">
                                            <span class="amount-label">{{ $t('employer.traz.debit') }}:</span>
                                            <span class="amount-value debit">{{ formatCurrency(account.MandeLast_Bed) }}</span>
                                        </div>
                                        <div class="amount-col">
                                            <span class="amount-label">{{ $t('employer.traz.credit') }}:</span>
                                            <span class="amount-value credit">{{ formatCurrency(account.MandeLast_Bes) }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Actions -->
                                <div class="card-actions">
                                    <button 
                                        v-if="account.HasDetails" 
                                        class="details-btn"
                                        @click.stop=""
                                    >
                                        <span class="btn-icon">📋</span>
                                        {{ $t('employer.traz.view_details') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Child Account Cards (when expanded) -->
                        <template v-if="account.expanded && account.children">
                            <div 
                                v-for="child in getVisibleChildren(account)" 
                                :key="`child-${child.HandCode}`"
                                class="account-card child-card"
                                :style="{ marginRight: `${(child.Level || 1) * 20}px` }"
                            >
                                <div class="card-header child-header">
                                    <div class="account-code-header">
                                        <span class="code-badge child-badge">
                                            {{ child.HandCode }}
                                        </span>
                                        <span class="account-level">{{ $t('employer.traz.level') }} {{ child.Level || 1 }}</span>
                                    </div>
                                    <h4 class="account-title child-title">{{ child.HandTitle }}</h4>
                                </div>
                                
                                <div class="card-body">
                                    <!-- Child account content similar to parent -->
                                    <div class="balance-section">
                                        <h5 class="section-title">{{ $t('employer.traz.opening_balance') }}</h5>
                                        <div class="amount-row">
                                            <div class="amount-col">
                                                <span class="amount-label">{{ $t('employer.traz.debit') }}:</span>
                                                <span class="amount-value debit">{{ formatCurrency(child.MandeFirst_Bed) }}</span>
                                            </div>
                                            <div class="amount-col">
                                                <span class="amount-label">{{ $t('employer.traz.credit') }}:</span>
                                                <span class="amount-value credit">{{ formatCurrency(child.MandeFirst_Bes) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div class="balance-section">
                                        <h5 class="section-title">{{ $t('employer.traz.period_performance') }}</h5>
                                        <div class="amount-row">
                                            <div class="amount-col">
                                                <span class="amount-label">{{ $t('employer.traz.debit') }}:</span>
                                                <span class="amount-value debit">{{ formatCurrency(child.Sum_Bed) }}</span>
                                            </div>
                                            <div class="amount-col">
                                                <span class="amount-label">{{ $t('employer.traz.credit') }}:</span>
                                                <span class="amount-value credit">{{ formatCurrency(child.Sum_Bes) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div class="balance-section">
                                        <h5 class="section-title">{{ $t('employer.traz.closing_balance') }}</h5>
                                        <div class="amount-row">
                                            <div class="amount-col">
                                                <span class="amount-label">{{ $t('employer.traz.debit') }}:</span>
                                                <span class="amount-value debit">{{ formatCurrency(child.MandeLast_Bed) }}</span>
                                            </div>
                                            <div class="amount-col">
                                                <span class="amount-label">{{ $t('employer.traz.credit') }}:</span>
                                                <span class="amount-value credit">{{ formatCurrency(child.MandeLast_Bes) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div class="card-actions">
                                        <button 
                                            v-if="child.HasDetails" 
                                            class="details-btn"
                                            @click.stop=""
                                        >
                                            <span class="btn-icon">📋</span>
                                            {{ $t('employer.traz.details') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </template>
                    
                    <!-- No Results -->
                    <div v-if="filteredAccounts.length === 0 && !loading" class="no-results-card">
                        <div class="empty-state">
                            <div class="empty-icon">📊</div>
                            <h4 v-if="searchQuery">{{ $t('employer.traz.no_search_results', { query: searchQuery }) }}</h4>
                            <h4 v-else>{{ $t('employer.traz.no_accounts_found') }}</h4>
                            <p v-if="searchQuery" class="empty-text">{{ $t('employer.traz.change_search') }}</p>
                            <p v-else class="empty-text">{{ $t('employer.traz.no_accounts_period') }}</p>
                        </div>
                    </div>
                </div>
                
                <!-- Pagination -->
                <div class="pagination-controls" v-if="totalPages > 1">
                    <button 
                        @click="prevPage" 
                        :disabled="currentPage === 1"
                        class="pagination-btn"
                    >
                        <span class="pagination-icon">←</span>
                        {{ $t('employer.traz.previous') }}
                    </button>
                    <span class="page-info">
                        {{ $t('employer.traz.page') }} {{ currentPage }} {{ $t('employer.traz.of') }} {{ totalPages }}
                    </span>
                    <button 
                        @click="nextPage" 
                        :disabled="currentPage === totalPages"
                        class="pagination-btn"
                    >
                        {{ $t('employer.traz.next') }}
                        <span class="pagination-icon">→</span>
                    </button>
                    <div class="items-per-page">
                        <label>{{ $t('employer.traz.show') }}:</label>
                        <select v-model="itemsPerPage" @change="onItemsPerPageChange">
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Account Details Modal -->
        <div v-if="showDetailsModal" class="modal-overlay" @click="closeDetailsModal">
            <div class="modal-content details-modal" @click.stop>
                <!-- Your existing modal content -->
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import DatePicker from 'vue3-persian-datetime-picker'
import { employerService, accountingService } from '@/services/api'
import moment from 'moment-jalaali'


export default {
    name: 'TarazView',
    components: {
        Navbar,
        DatePicker
    },
    setup() {
        const router = useRouter()
        
        // State
        const loading = ref(false)
        const error = ref(null)
        const searchQuery = ref('')
        const currentPage = ref(1)
        const itemsPerPage = ref(10)
        const showSummary = ref(true)
        const tarazData = ref([])
        const totals = ref(null)
        
        // Currency
        const arzList = ref([])
        const selectedArz = ref(1)
        const selectedMonth = ref('')
        const loadingArz = ref(false)
        const selectedCmLevel = ref(1)
        
        // Date range
        const selectedDateRange = ref('this_month')
        const customDateFrom = ref('')
        const customDateTo = ref('')
        
        // Modal
        const showDetailsModal = ref(false)
        const selectedAccount = ref(null)
        const accountDetails = ref([])
        const accountDetailsLoading = ref(false)
        const accountDetailsError = ref(null)

        // Computed properties
        const filteredAccounts = computed(() => {
            if (!searchQuery.value.trim()) {
                return tarazData.value.filter(account => !account.parentCode)
            }
            
            const query = searchQuery.value.trim().toLowerCase()
            return tarazData.value.filter(account => {
                if (account.parentCode) return false
                
                const matchesCode = (account.HandCode || '').toLowerCase().includes(query)
                const matchesName = (account.HandTitle || '').toLowerCase().includes(query)
                
                return matchesCode || matchesName
            })
        })

        const persianMonths = [
            'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
            'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
        ]

        const selectedMonthName = computed(() => {
            if (!selectedMonth.value) return ''
            return persianMonths[selectedMonth.value - 1]
        })

        const onMonthChange = () => {
            console.log('Selected month changed to:', selectedMonth.value)
            fetchTarazData()
        }

        const selectedCmLevelText = computed(() => {
            switch (selectedCmLevel.value) {
                case 1: return 'گروه'
                case 2: return 'کل'
                case 3: return 'معین'
                case 4: return 'تفضیلی'
                default: return 'گروه'
            }
        })

        const paginatedAccounts = computed(() => {
            const startIndex = (currentPage.value - 1) * itemsPerPage.value
            const endIndex = startIndex + itemsPerPage.value
            return filteredAccounts.value.slice(startIndex, endIndex)
        })

        const totalPages = computed(() => {
            return Math.ceil(filteredAccounts.value.length / itemsPerPage.value)
        })

        // Totals computed from API response
        const totalFinalDebit = computed(() => {
            return totals.value?.total_final_debit || 0
        })

        const totalFinalCredit = computed(() => {
            return totals.value?.total_final_credit || 0
        })

        const balanceDifference = computed(() => {
            return totalFinalDebit.value - totalFinalCredit.value
        })

        const currentDate = computed(() => {
            return new Date().toLocaleDateString('fa-IR')
        })

        const selectedArzName = computed(() => {
            const arz = arzList.value.find(a => a.idd === selectedArz.value)
            return arz ? arz.nam : 'ریال'
        })

        // Methods
        const navigateToDashboard = () => {
            router.push('/employer/dashboard')
        }

        const fetchAllArz = async () => {
            loadingArz.value = true
            try {
                const response = await accountingService.getAllArz()
                if (response.data.success) {
                    arzList.value = response.data.data
                    console.log('✅ ARZ list loaded:', arzList.value)
                }
            } catch (err) {
                console.error('❌ Error fetching ARZ:', err)
            } finally {
                loadingArz.value = false
            }
        }

        const getDateRangeText = () => {
            switch (selectedDateRange.value) {
                case 'today': return 'امروز'
                case 'yesterday': return 'دیروز'
                case 'this_week': return 'این هفته'
                case 'this_month': return 'این ماه'
                case 'last_month': return 'ماه گذشته'
                case 'custom': return 'سفارشی'
                default: return 'این ماه'
            }
        }

        const calculateDateRange = () => {
            const now = new Date()
            let dateFrom, dateTo
            
            switch (selectedDateRange.value) {
                case 'today': {
                    dateFrom = dateTo = now.toISOString().split('T')[0]
                    break
                }
                case 'yesterday': {
                    const yesterday = new Date(now)
                    yesterday.setDate(yesterday.getDate() - 1)
                    dateFrom = dateTo = yesterday.toISOString().split('T')[0]
                    break
                }
                case 'this_week': {
                    const startOfWeek = new Date(now)
                    startOfWeek.setDate(now.getDate() - now.getDay())
                    dateFrom = startOfWeek.toISOString().split('T')[0]
                    dateTo = now.toISOString().split('T')[0]
                    break
                }
                case 'this_month': {
                    dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
                    dateTo = now.toISOString().split('T')[0]
                    break
                }
                case 'last_month': {
                    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                    dateFrom = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1).toISOString().split('T')[0]
                    dateTo = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).toISOString().split('T')[0]
                    break
                }
                case 'custom': {
                    dateFrom = customDateFrom.value
                    dateTo = customDateTo.value
                    break
                }
                default: {
                    dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
                    dateTo = now.toISOString().split('T')[0]
                }
            }
            
            return { dateFrom, dateTo }
        }

        const onCmLevelChange = () => {
            currentPage.value = 1
            fetchTarazData()
        }

        const fetchTarazData = async () => {
            loading.value = true
            error.value = null
            console.log("mah is " + selectedMonth.value)
            try {
                const dateRange = calculateDateRange()
                
                const tarazRequest = {
                    arz: selectedArz.value,
                    date_from: dateRange.dateFrom,
                    date_to: dateRange.dateTo,
                    include_summary: showSummary.value,
                    cm: selectedCmLevel.value,  // Add this
                    mah: selectedMonth.value
                }
                
                console.log('🔧 Fetching taraz data with:', tarazRequest)
                
                const response = await employerService.getTarazData(tarazRequest)
                
                if (response.data.success) {
                    tarazData.value = response.data.data.map(account => ({
                        ...account,
                        expanded: false
                    }))
                    totals.value = {
                        total_pre_debit: response.data.total_pre_debit || 0,
                        total_pre_credit: response.data.total_pre_credit || 0,
                        total_today_debit: response.data.total_today_debit || 0,
                        total_today_credit: response.data.total_today_credit || 0,
                        total_final_debit: response.data.total_final_debit || 0,
                        total_final_credit: response.data.total_final_credit || 0
                    }
                    
                    console.log(`✅ Taraz data loaded: ${tarazData.value.length} accounts`)
                } else {
                    error.value = response.data.message || 'خطا در دریافت اطلاعات تراز'
                }
            } catch (err) {
                console.error('❌ Error fetching taraz data:', err)
                error.value = err.response?.data?.detail || err.message || 'خطا در دریافت اطلاعات تراز'
            } finally {
                loading.value = false
            }
        }

        const onArzChange = () => {
            currentPage.value = 1
            fetchTarazData()
        }

        const onDateRangeChange = () => {
            if (selectedDateRange.value !== 'custom') {
                currentPage.value = 1
                fetchTarazData()
            }
        }

        const applyCustomDates = () => {
            if (customDateFrom.value && customDateTo.value) {
                currentPage.value = 1
                fetchTarazData()
            }
        }

        const getRowClass = (account) => {
            return `level-${account.Level || 1}`
        }

        const getIndent = (account) => {
            const level = account.Level || 1
            return 10 + (level - 1) * 20
        }

        const toggleAccount = (account) => {
            if (account.HasChildren) {
                account.expanded = !account.expanded
            }
        }

        const getVisibleChildren = (account) => {
            if (!account.expanded || !account.children) return []
            return account.children
        }

        const openAccountDetails = async (account) => {
            selectedAccount.value = account
            showDetailsModal.value = true
            accountDetailsLoading.value = true
            accountDetailsError.value = null
            accountDetails.value = []
            
            try {
                // You need to implement this endpoint in your backend
                // const response = await employerService.getAccountDetails(
                //     account.HandCode,
                //     calculateDateRange().dateFrom,
                //     calculateDateRange().dateTo,
                //     selectedArz.value
                // )
                
                // For now, show sample data
                await new Promise(resolve => setTimeout(resolve, 800))
                
                accountDetails.value = [
                    {
                        date: '1402/12/15',
                        documentNumber: '12345',
                        description: 'فروش کالا',
                        counterAccount: 'صندوق',
                        debit: account.MandeLast_Bed > 0 ? account.MandeLast_Bed : 0,
                        credit: account.MandeLast_Bes > 0 ? account.MandeLast_Bes : 0,
                        balance: account.MandeLast_Bed - account.MandeLast_Bes,
                        reference: 'فاکتور 123'
                    }
                ]
                
            } catch (err) {
                console.error('Error fetching account details:', err)
                accountDetailsError.value = 'خطا در دریافت اطلاعات جزئیات'
            } finally {
                accountDetailsLoading.value = false
            }
        }

        const closeDetailsModal = () => {
            showDetailsModal.value = false
            selectedAccount.value = null
            accountDetails.value = []
            accountDetailsError.value = null
        }

        const printBalanceSheet = () => {
            const printContent = document.querySelector('.balance-sheet-table')?.innerHTML
            if (!printContent) return
            
            const printWindow = window.open('', '_blank')
            printWindow.document.write(`
                <html dir="rtl">
                <head>
                    <title>چاپ تراز ۶ ستونی</title>
                    <style>
                        body { 
                            font-family: Tahoma, Arial, sans-serif; 
                            margin: 20px;
                            direction: rtl;
                        }
                        table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            margin-top: 20px;
                        }
                        th, td { 
                            border: 1px solid #ddd; 
                            padding: 8px; 
                            text-align: right;
                        }
                        th { 
                            background-color: #f5f5f5; 
                            font-weight: bold;
                        }
                        .debit-amount { color: #e74c3c; }
                        .credit-amount { color: #27ae60; }
                        .summary-row { background-color: #f8f9fa; }
                        .level-1 { background-color: #e3f2fd; }
                        .level-2 { background-color: #f3e5f5; }
                        .level-3 { background-color: #e8f5e8; }
                        @media print {
                            body { margin: 0; }
                        }
                    </style>
                </head>
                <body>
                    <h2>تراز ۶ ستونی</h2>
                    <div>تاریخ: ${currentDate.value}</div>
                    <div>ارز: ${selectedArzName.value}</div>
                    <div>بازه زمانی: ${getDateRangeText()}</div>
                    ${printContent}
                </body>
                </html>
            `)
            printWindow.document.close()
            printWindow.focus()
            setTimeout(() => {
                printWindow.print()
            }, 500)
        }

        const exportToExcel = () => {
            let csvContent = "کد حساب,نام حساب,مانده اول بدهکار,مانده اول بستانکار,بدهکار دوره,بستانکار دوره,مانده آخر بدهکار,مانده آخر بستانکار\\n"
            
            tarazData.value.forEach(account => {
                csvContent += `"${account.HandCode}","${account.HandTitle}",${account.MandeFirst_Bed},${account.MandeFirst_Bes},${account.Sum_Bed},${account.Sum_Bes},${account.MandeLast_Bed},${account.MandeLast_Bes}\\n`
            })
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = `تراز-۶-ستونی-${currentDate.value}.csv`
            link.click()
        }

        const onItemsPerPageChange = () => {
            currentPage.value = 1
        }

        const nextPage = () => {
            if (currentPage.value < totalPages.value) {
                currentPage.value++
            }
        }

        const prevPage = () => {
            if (currentPage.value > 1) {
                currentPage.value--
            }
        }

        const formatCurrency = (amount) => {
            if (!amount && amount !== 0) return '۰'
            const arz = arzList.value.find(a => a.idd === selectedArz.value)
            const currencySign = arz ? arz.sign : 'ریال'
            return new Intl.NumberFormat('fa-IR').format(amount) + ' ' + currencySign
        }

        const formatRemainingCash = (amount) => {
            if (!amount && amount !== 0) return '۰'
            const absoluteAmount = Math.abs(amount)
            const arz = arzList.value.find(a => a.idd === selectedArz.value)
            const currencySign = arz ? arz.sign : 'ریال'
            return new Intl.NumberFormat('fa-IR').format(absoluteAmount) + ' ' + currencySign
        }

        const formatBestanKari = (amount) => {
            if (!amount && amount !== 0) return '۰'
            if (amount > 0){
                return 'بدهکار'
            }else {
                return 'بستانکار'
            }
        }

        // Initialize
        onMounted(async () => {
            await fetchAllArz()
            
            const currentPersianMonth = moment().jMonth() + 1 // 1-12
            selectedMonth.value = currentPersianMonth

            await fetchTarazData()
        })

        return {
            // State
            loading,
            error,
            searchQuery,
            currentPage,
            itemsPerPage,
            showSummary,
            selectedDateRange,
            customDateFrom,
            customDateTo,
            showDetailsModal,
            selectedAccount,
            accountDetails,
            accountDetailsLoading,
            accountDetailsError,
            arzList,
            selectedArz,
            loadingArz,
            totals,
            
            // Computed
            filteredAccounts,
            paginatedAccounts,
            totalPages,
            totalFinalDebit,
            totalFinalCredit,
            balanceDifference,
            currentDate,
            selectedArzName,
            persianMonths,
            
            // Computed
            selectedMonthName,
            selectedMonth,
            
            // Methods
            navigateToDashboard,
            fetchTarazData,
            onArzChange,
            onDateRangeChange,
            applyCustomDates,
            getDateRangeText,
            getRowClass,
            getIndent,
            toggleAccount,
            getVisibleChildren,
            openAccountDetails,
            closeDetailsModal,
            printBalanceSheet,
            exportToExcel,
            onItemsPerPageChange,
            nextPage,
            prevPage,
            formatCurrency,
            formatRemainingCash,
            formatBestanKari,
            onMonthChange,
            selectedCmLevel,
            selectedCmLevelText,
            onCmLevelChange,
        }
    }
}
</script>

<style scoped>
/* Modern Card-Based Styles */

.bank-app {
    min-height: 100vh;
    background: var(--bg);
    padding: 20px;
}

/* Account Summary */
.account-summary {
    background: var(--card-bg);
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 4px 20px var(--shadow-1);
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-right: 5px solid var(--primary);
}

.account-type {
    font-size: 18px;
    color: var(--text);
    font-weight: 600;
}

.account-balance {
    display: flex;
    align-items: center;
    gap: 10px;
}

.balance-amount {
    font-size: 22px;
    font-weight: 700;
    color: var(--orange-1);
    background: rgba(249, 115, 22, 0.1);
    padding: 8px 16px;
    border-radius: 10px;
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.1rem;
  color: var(--heading);
  margin: 0;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  color: var(--primary);
}

.back-arrow:hover {
  background-color: var(--hover);
}

.section-title {
    text-align: center;
    color: var(--heading);
    font-size: 24px;
    font-weight: 700;
    margin: 0;
}

/* Action Bar */
.action-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 15px;
    background: var(--card-bg);
    border-radius: 15px;
    margin-bottom: 20px;
    box-shadow: 0 4px 20px var(--shadow-1);
    flex-wrap: wrap;
    border: 1px solid var(--card-border);
}

.action-bar button {
    padding: 10px 16px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.print-btn {
    background: rgba(249, 115, 22, 0.1);
    color: var(--orange-1);
}

.export-btn {
    background: rgba(59, 130, 246, 0.1);
    color: var(--primary);
}

.refresh-btn {
    background: rgba(16, 185, 129, 0.1);
    color: var(--green);
}

.refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Currency Selector */
.currency-selector {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--bg);
    padding: 8px 12px;
    border-radius: 10px;
    flex: 1;
    min-width: 200px;
    border: 1px solid var(--card-border);
}

.currency-label {
    font-size: 14px;
    color: var(--subtext);
    white-space: nowrap;
}

.currency-select {
    flex: 1;
    padding: 8px 12px;
    border: 2px solid var(--card-border);
    border-radius: 8px;
    background: var(--card-bg);
    font-size: 14px;
    color: var(--text);
    transition: border-color 0.3s ease;
}

.currency-select:focus {
    outline: none;
    border-color: var(--primary);
}

/* Date Range Selector */
.date-range-selector {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--bg);
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid var(--card-border);
}

.custom-dates {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.persian-date-picker {
    padding: 8px 12px;
    border: 2px solid var(--card-border);
    border-radius: 8px;
    width: 140px;
    font-size: 14px;
    background: var(--card-bg);
    color: var(--text);
}

.apply-date-btn {
    background: var(--primary);
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
}

.apply-date-btn:hover {
    background: var(--primary-hover);
}

/* Search Container */
.search-container {
    flex: 1;
    position: relative;
    min-width: 250px;
}

.search-input {
    width: 100%;
    padding: 10px 40px 10px 15px;
    border: 2px solid var(--card-border);
    border-radius: 10px;
    font-size: 14px;
    transition: all 0.3s ease;
    background: var(--card-bg);
    color: var(--text);
}

.search-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--ring);
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--subtext);
}

/* Summary Cards */
.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 20px;
}

.summary-card {
    background: var(--card-bg);
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 20px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.total-card {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
}

.summary-card-header {
    padding: 20px;
    border-bottom: 1px solid rgba(255,255,255,0.2);
}

.summary-card-body {
    padding: 20px;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
}

.summary-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.summary-label {
    font-size: 13px;
    opacity: 0.9;
}

.summary-value {
    font-size: 16px;
    font-weight: 700;
}

.summary-value.debit {
    color: var(--orange-1);
}

.summary-value.credit {
    color: var(--green);
}

.balance-item {
    grid-column: 1 / -1;
    padding-top: 15px;
    border-top: 1px solid rgba(255,255,255,0.2);
    margin-top: 10px;
}

/* Account Cards Container */
.account-cards-container {
    background: var(--card-bg);
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 20px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.cards-info-header {
    padding: 20px;
    background: var(--bg);
    border-bottom: 1px solid var(--card-border);
}

.cards-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    align-items: center;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--subtext);
    background: var(--card-bg);
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid var(--card-border);
}

.stat-icon {
    font-size: 16px;
}

/* Account Cards Grid */
.account-cards-grid {
    padding: 20px;
    padding: 45px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
}

@media (max-width: 768px) {
    .account-cards-grid {
        grid-template-columns: 1fr;
    }
}

/* Account Card */
.account-card {
    background: var(--card-bg);
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 4px 15px var(--shadow-1);
    transition: all 0.3s ease;
    border: 1px solid var(--card-border);
}

.account-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px var(--shadow-2);
}

.account-card.has-children .card-header {
    cursor: pointer;
}

.account-card.expanded {
    border-color: var(--primary);
    box-shadow: 0 6px 20px var(--ring);
}

/* Card Header */
.card-header {
    padding: 20px;
    border-bottom: 1px solid var(--card-border);
    background: var(--bg);
}

.account-code-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.expand-icon {
    font-size: 18px;
    color: var(--primary);
    cursor: pointer;
    transition: transform 0.3s ease;
}

.code-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    color: white;
}

.level-1-badge {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
}

.level-2-badge {
    background: linear-gradient(135deg, var(--orange-1) 0%, var(--orange-2) 100%);
}

.level-3-badge {
    background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%);
    color: #333;
}

.level-4-badge {
    background: linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%);
    color: #333;
}

.level-5-badge {
    background: linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%);
    color: #333;
}

.child-badge {
    background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
    color: #666;
}

.summary-badge {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    font-size: 16px;
}

.account-level {
    font-size: 12px;
    color: var(--subtext);
    background: var(--card-bg);
    padding: 2px 8px;
    border-radius: 10px;
    border: 1px solid var(--card-border);
}

.account-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--heading);
    margin: 0;
    line-height: 1.4;
}

.child-title {
    font-size: 16px;
    color: var(--subtext);
    font-weight: 600;
}

/* Card Body */
.card-body {
    padding: 20px;
    overflow: hidden;
}

.balance-section {
    margin-bottom: 20px;
    overflow: hidden;
}

.balance-section:last-child {
    margin-bottom: 0;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    color: var(--subtext);
    margin-bottom: 12px;
    font-weight: 600;
}

.section-icon {
    font-size: 18px;
}

.amount-row {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
}

@media (max-width: 480px) {
    .amount-row {
        flex-direction: column;
        gap: 10px;
    }
}

.amount-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
}

.amount-label {
    font-size: 13px;
    color: var(--subtext);
    white-space: nowrap;
}

.amount-value {
    font-size: 14px;
    font-weight: 700;
    word-break: break-all;
    overflow-wrap: break-word;
    line-height: 1.3;
    min-height: 1.3em;
    direction: ltr;
    text-align: left;
    padding: 4px 0;
    color: var(--text);
}

.amount-value.debit {
    color: var(--orange-1);
}

.amount-value.credit {
    color: var(--green);
}

/* Card Actions */
.card-actions {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid var(--card-border);
}

.details-btn {
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.details-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px var(--shadow-2);
}

.btn-icon {
    font-size: 16px;
}

/* Level-based card styling */
.level-1 {
    border-right: 5px solid var(--primary);
}

.level-2 {
    border-right: 5px solid var(--orange-1);
}

.level-3 {
    border-right: 5px solid #a1c4fd;
}

.level-4 {
    border-right: 5px solid #84fab0;
}

.level-5 {
    border-right: 5px solid #d4fc79;
}

.child-card {
    border-right: 5px solid #a8edea;
    background: var(--bg);
}

/* Summary Card */
.account-card.summary-card {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
}

.account-card.summary-card .account-title,
.account-card.summary-card .section-title,
.account-card.summary-card .amount-label,
.account-card.summary-card .amount-value {
    color: white;
}

.account-card.summary-card .section-title {
    opacity: 0.9;
}

.account-card.summary-card .amount-label {
    opacity: 0.8;
}

/* No Results Card */
.no-results-card {
    grid-column: 1 / -1;
    text-align: center;
    padding: 40px 20px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
}

.empty-icon {
    font-size: 60px;
    color: var(--muted-2);
}

.empty-state h4 {
    color: var(--heading);
    margin: 0;
}

.empty-text {
    color: var(--subtext);
    font-size: 14px;
}

/* Loading State */
.loading-state {
    padding: 60px 20px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.loading-spinner {
    width: 50px;
    height: 50px;
    border: 5px solid var(--card-border);
    border-top: 5px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
    padding: 40px 20px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 20px;
}

.error-state h4 {
    color: var(--orange-1);
    margin-bottom: 10px;
}

.error-state p {
    color: var(--subtext);
    margin-bottom: 20px;
}

.retry-btn {
    padding: 10px 25px;
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.retry-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px var(--shadow-2);
}

.items-per-page {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--card-bg);
    padding: 6px 12px;
    border-radius: 10px;
    border: 1px solid var(--card-border);
}

.items-per-page label {
    font-size: 13px;
    color: var(--subtext);
}

.items-per-page select {
    border: none;
    background: none;
    font-size: 14px;
    color: var(--text);
    outline: none;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .account-cards-grid {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    }
}

@media (max-width: 992px) {
    .action-bar {
        flex-direction: column;
        align-items: stretch;
    }
    
    .currency-selector,
    .date-range-selector,
    .search-container {
        width: 100%;
    }
    
    .summary-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .bank-app {
        padding: 10px;
    }
    
    .section-title {
        font-size: 20px;
    }
    
    .account-summary {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }
    
    .cards-stats {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media (max-width: 480px) {
    .action-bar button {
        width: 100%;
        justify-content: center;
    }
    
    .custom-dates {
        flex-direction: column;
        width: 100%;
    }
    
    .persian-date-picker {
        width: 100%;
    }
    
    .apply-date-btn {
        width: 100%;
    }
}

/* For very small screens */
@media (max-width: 360px) {
    .amount-value {
        font-size: 13px;
    }
    
    .section-title {
        font-size: 14px;
    }
    
    .account-title {
        font-size: 16px;
    }
}

/* Ensure numbers don't overflow containers */
.card-header,
.card-body,
.balance-section {
    max-width: 100%;
    box-sizing: border-box;
}

/* Alternative: Use ellipsis for very long numbers */
.amount-value.truncated {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    direction: ltr;
    text-align: left;
}

/* Add a tooltip on hover for truncated numbers */
.amount-value.truncated:hover::after {
    content: attr(data-full-value);
    position: absolute;
    background: var(--heading);
    color: var(--bg);
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 1000;
    white-space: nowrap;
    margin-top: 25px;
    margin-right: -10px;
}

/* Or use a scrollable container for numbers */
.amount-value.scrollable {
    overflow-x: auto;
    white-space: nowrap;
    padding: 4px 8px;
    background: var(--bg);
    border-radius: 4px;
    border: 1px solid var(--card-border);
    max-width: 100%;
    direction: ltr;
    scrollbar-width: thin;
}

.amount-value.scrollable::-webkit-scrollbar {
    height: 4px;
}

.amount-value.scrollable::-webkit-scrollbar-track {
    background: var(--card-border);
    border-radius: 2px;
}

.amount-value.scrollable::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 2px;
}

.amount-value.scrollable::-webkit-scrollbar-thumb:hover {
    background: var(--primary-hover);
}

/* CM Level Selector */
.cm-level-selector {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--bg);
    padding: 8px 12px;
    border-radius: 10px;
    flex: 1;
    min-width: 200px;
    border: 1px solid var(--card-border);
}

.cm-label {
    font-size: 14px;
    color: var(--subtext);
    white-space: nowrap;
}

.cm-select {
    flex: 1;
    padding: 8px 12px;
    border: 2px solid var(--card-border);
    border-radius: 8px;
    background: var(--card-bg);
    font-size: 14px;
    color: var(--text);
    transition: border-color 0.3s ease;
    text-align: right;
}

.cm-select:focus {
    outline: none;
    border-color: var(--green);
}

.cm-select option {
    text-align: right;
    direction: rtl;
}

/* Update action-bar for responsive design */
@media (max-width: 1200px) {
    .action-bar {
        gap: 10px;
    }
    
    .currency-selector,
    .cm-level-selector,
    .date-range-selector {
        min-width: 180px;
    }
}

@media (max-width: 768px) {
    .action-bar {
        flex-direction: column;
        align-items: stretch;
    }
    
    .currency-selector,
    .cm-level-selector,
    .date-range-selector,
    .search-container {
        width: 100%;
        min-width: auto;
    }
}
</style>